import { z } from "zod";
import { absentOrBlankAsNull, blankAsNull, unset } from "./fields";
import { optional, photoUrl, storableText } from "./appointments";
import { recordId } from "./contracts";
import { photoContentType } from "./photo-types";

export const propertyType = z.enum(["luxury", "leaseup", "affordable", "senior", "student"]);
export type PropertyType = z.infer<typeof propertyType>;

export const propertyStructure = z.enum(["highrise", "midrise", "garden style", "historic", "botique", "others"]);
export type PropertyStructure = z.infer<typeof propertyStructure>;

export const propertyClass = z.enum(["A", "B", "C"]);
export type PropertyClass = z.infer<typeof propertyClass>;

export const propertyDetails = z.object({
  image: z.string().nullable(),
  street: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  types: z.array(propertyType),
  structures: z.array(propertyStructure),
  classes: z.array(propertyClass),
  yearBuilt: z.number().nullable(),
  floors: z.number().nullable(),
  createdAt: z.string(),
});
export type PropertyDetails = z.infer<typeof propertyDetails>;

export const propertyBillTo = z.object({
  name: z.string().nullable(),
  address: z.string().nullable(),
});
export type PropertyBillTo = z.infer<typeof propertyBillTo>;

export const property = z.object({
  id: recordId,
  name: z.string(),
  address: z.string().nullable(),
  accessNotes: z.string().nullable(),
  customer: z.object({ id: recordId, name: z.string() }).nullable().optional(),
  details: propertyDetails.optional(),
  billTo: propertyBillTo.optional(),
});
export type Property = z.infer<typeof property>;

export const onSiteContact = z.object({
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  image: z.string().nullable(),
});
export type OnSiteContact = z.infer<typeof onSiteContact>;

export const propertyRecord = property.extend({
  unitCount: z.number().int().nonnegative().optional(),
  appointmentCount: z.number().int().nonnegative().optional(),
  onSiteContact: onSiteContact.nullable().optional(),
  leadContact: onSiteContact.nullable().optional(),
});
export type PropertyRecord = z.infer<typeof propertyRecord>;

export const PROPERTY_PAGE_SIZE_MAX = 500;

const exactName = z
  .string()
  .max(200)
  .refine((value) => !value.includes("\0"), "no NUL characters");

export const propertyReadQuery = z.object({
  actingOrganization: optional(recordId.toLowerCase()),
});
export type PropertyReadQuery = z.infer<typeof propertyReadQuery>;

export const propertySortKey = z.enum(["createdAt", "name"]);
export type PropertySortKey = z.infer<typeof propertySortKey>;

export const propertyListQuery = propertyReadQuery.extend({
  customer: optional(recordId.toLowerCase()),
  vendor: optional(recordId.toLowerCase()),
  assignedTo: optional(recordId.toLowerCase()),
  type: optional(propertyType),
  structure: optional(propertyStructure),
  class: optional(propertyClass),
  name: optional(exactName),
  sort: z.preprocess(unset, propertySortKey.default("createdAt")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("desc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(PROPERTY_PAGE_SIZE_MAX).default(25)),
});
export type PropertyListQuery = z.infer<typeof propertyListQuery>;

export const propertyPage = z.object({
  items: z.array(property),
  total: z.number().int().nonnegative(),
});
export type PropertyPage = z.infer<typeof propertyPage>;

export const PROPERTY_NAME_MAX_LENGTH = 200;
export const PROPERTY_DESCRIPTION_MAX_LENGTH = 5000;
export const PROPERTY_ADDRESS_MAX_LENGTH = 500;
export const PROPERTY_ADDRESS_PART_MAX_LENGTH = 200;
export const BILL_TO_NAME_MAX_LENGTH = 200;
export const BILL_TO_ADDRESS_MAX_LENGTH = 1000;
export const YEAR_BUILT_MIN = 1800;
export const FLOORS_MAX = 1000;

const optionalText = (maxLength: number) => z.preprocess(absentOrBlankAsNull, storableText(maxLength).nullable());

const clearableText = (maxLength: number) => z.preprocess(blankAsNull, storableText(maxLength).nullable().optional());

const distinct = <T>(values: T[]): T[] => [...new Set(values)];

const atLeastOne = <T extends z.ZodTypeAny>(option: T) => z.array(option).min(1).transform(distinct);

const WEB_SCHEMES: ReadonlySet<string> = new Set(["http:", "https:"]);

const isWebAddress = (value: string): boolean => {
  try {
    return WEB_SCHEMES.has(new URL(value).protocol);
  } catch {
    return false;
  }
};

const webAddress = z.string().trim().max(2048).refine(isWebAddress, "an http or https address");

const notInTheFuture = (year: number) => year <= new Date().getUTCFullYear() + 1;

export const propertyDetailsRequest = z
  .object({
    name: storableText(PROPERTY_NAME_MAX_LENGTH),
    description: optionalText(PROPERTY_DESCRIPTION_MAX_LENGTH),
    yearBuilt: z.number().int().min(YEAR_BUILT_MIN).refine(notInTheFuture, "not in the future"),
    floors: z.number().int().min(1).max(FLOORS_MAX),
    types: atLeastOne(propertyType),
    structures: atLeastOne(propertyStructure),
    classes: atLeastOne(propertyClass),
    billToName: clearableText(BILL_TO_NAME_MAX_LENGTH),
    billToAddress: clearableText(BILL_TO_ADDRESS_MAX_LENGTH),
  })
  .strict();
export type PropertyDetailsRequest = {
  name: string;
  description?: string | null;
  yearBuilt: number;
  floors: number;
  types: PropertyType[];
  structures: PropertyStructure[];
  classes: PropertyClass[];
  billToName?: string | null;
  billToAddress?: string | null;
};

export const propertyCreateRequest = propertyDetailsRequest
  .extend({
    customer: recordId.toLowerCase(),
    address: storableText(PROPERTY_ADDRESS_MAX_LENGTH),
    website: z.preprocess(absentOrBlankAsNull, webAddress.nullable()),
    street: optionalText(PROPERTY_ADDRESS_PART_MAX_LENGTH),
    city: optionalText(PROPERTY_ADDRESS_PART_MAX_LENGTH),
    county: optionalText(PROPERTY_ADDRESS_PART_MAX_LENGTH),
    state: optionalText(PROPERTY_ADDRESS_PART_MAX_LENGTH),
    zip: z.preprocess(absentOrBlankAsNull, z.string().trim().regex(/^\d{1,10}$/, "digits only").nullable()),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
  })
  .strict();
export type PropertyCreateRequest = PropertyDetailsRequest & {
  customer: string;
  address: string;
  website?: string | null;
  street?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
  zip?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export const propertyImageUploadRequest = z.object({ contentType: photoContentType }).strict();
export type PropertyImageUploadRequest = z.infer<typeof propertyImageUploadRequest>;

export const propertyImageRequest = z.object({ imageUrl: photoUrl }).strict();
export type PropertyImageRequest = z.infer<typeof propertyImageRequest>;
